import {
  buildingPartRequirementAiSchema,
  buildingPartRequirementSchema,
  elementRequirementSchema,
  BUILDING_PART_MATERIAL_LABELS,
  BUILDING_PART_RANGE_PARAMETER_LABELS,
  type BuildingPartConstraint,
  type BuildingPartConstraintAi,
  type BuildingPartLimitComparison,
  type BuildingPartMaterial,
  type BuildingPartRequirement,
  type BuildingPartRequirementAggregated,
  type BuildingPartRequirementAi,
  type BuildingPartRequirementReading,
  type BuildingPartRequirementSource,
  type BuildingRequirementAppliesTo,
  type ElementRequirement,
  type ElementRequirementAggregated,
  type LegacyBuildingPartRequirement,
} from '../models/building-part-requirement.model';
import { getTypbeteckning } from '../construction/typbeteckningar.data';
import {
  extractBuildingReference,
  normalizeBuildingInstanceKey,
} from './project-file-building-key.helpers';
import { isProjectFileExtractionOutputV1 } from '../models/project-file-extraction.model';
import type { ProjectFile } from '../models/project-file-batch.model';
import { assertUnreachable } from './function.helpers';
import { guards } from './typeguards.helpers';

/**
 * Narrows the OpenAI-flat constraint into the stored discriminated union.
 * Returns null when `kind` and sibling fields disagree (empty material list, missing range id).
 */
export const narrowBuildingPartConstraint = (
  raw: BuildingPartConstraintAi,
): BuildingPartConstraint | null => {
  switch (raw.kind) {
    case 'material': {
      if (raw.allowed == null || raw.allowed.length === 0) return null;
      return { kind: 'material', allowed: raw.allowed };
    }
    case 'range': {
      if (raw.parameter_id == null) return null;
      return {
        kind: 'range',
        parameter_id: raw.parameter_id,
        min: raw.min,
        max: raw.max,
      };
    }
    case 'limit': {
      const unit = raw.unit?.trim() ?? '';
      if (raw.comparison == null || raw.number == null || unit === '') {
        return null;
      }
      return {
        kind: 'limit',
        comparison: raw.comparison,
        number: raw.number,
        unit,
      };
    }
    case 'text': {
      const text = raw.text?.trim() ?? '';
      if (text === '') return null;
      return { kind: 'text', text, required: raw.required ?? true };
    }
    default:
      return assertUnreachable(raw.kind);
  }
};

const blankToNull = (value: string | null): string | null => {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
};

const EMPTY_PRINTED_CODES = { sbef: null, littra: null };

/** An element row is the material requirement. A cap has no `type`. */
export const isElementRequirement = (
  row: object,
): row is ElementRequirement | ElementRequirementAggregated =>
  'type' in row && (row as { type?: unknown }).type === 'element';

/** A stated portion, 0–1. `100%` is 1. A count is not a portion. */
const parseShare = (
  value: string | number | null | undefined,
): number | null => {
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value < 0 || value > 1) return null;
    return value;
  }
  const text = value?.trim().replace(/\s+/g, '') ?? '';
  if (text === '') return null;
  const percent = /^(\d+(?:[.,]\d+)?)%$/.exec(text);
  if (percent?.[1] !== undefined) {
    const n = Number(percent[1].replace(',', '.')) / 100;
    return n >= 0 && n <= 1 ? n : null;
  }
  const fraction = /^(\d+)\/(\d+)$/.exec(text);
  if (fraction?.[1] !== undefined && fraction[2] !== undefined) {
    const denominator = Number(fraction[2]);
    if (denominator === 0) return null;
    const n = Number(fraction[1]) / denominator;
    return n >= 0 && n <= 1 ? n : null;
  }
  const asNumber = Number(text.replace(',', '.'));
  if (!Number.isFinite(asNumber) || asNumber < 0 || asNumber > 1) return null;
  return asNumber;
};

export const formatRequirementShare = (share: number): string => {
  if (Number.isInteger(share)) return String(share);
  const rounded = Math.round(share * 10000) / 10000;
  return String(rounded);
};

const isMaxBuildingHeight = (constraint: BuildingPartConstraint): boolean =>
  constraint.kind === 'range' && constraint.parameter_id === 'building_height';

const MATERIAL_LIST_SPLIT = /\s*(?:,|\/|;|\boch\b|\band\b)\s*/i;

/** A mapping label. A sentence or a joined heading is not one. */
const shortMappingValue = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim() ?? '';
  if (trimmed === '' || trimmed.length > 40) return null;
  if (/[.!?]/.test(trimmed) || trimmed.includes(' · ')) return null;
  return trimmed;
};

const materialList = (value: string): string[] => {
  const parts = value
    .split(MATERIAL_LIST_SPLIT)
    .map((part) => part.trim())
    .filter((part) => part !== '');
  if (parts.length < 2) return [];
  if (parts.some((part) => shortMappingValue(part) === null)) return [];
  return parts;
};

const partLabel = (
  row: Pick<LegacyBuildingPartRequirement, 'name' | 'source_label'>,
): string | null => {
  const named = shortMappingValue(row.name);
  if (named !== null) return named;
  return shortMappingValue(row.source_label);
};

/**
 * Drops a sentence and a row with no building part and no material.
 * A stated share is kept. An absent share stays null.
 * A material list is left for {@link expandMaterialRows}.
 */
const keepStoredRequirement = (
  row: LegacyBuildingPartRequirement,
): LegacyBuildingPartRequirement | null => {
  if (row.constraint.kind === 'range' || row.constraint.kind === 'limit') {
    return row;
  }
  if (row.constraint.kind === 'text') {
    return materialList(row.constraint.text).length > 1 ? row : null;
  }
  const raw = blankToNull(row.material);
  const part = partLabel(row);
  if (raw === null) {
    if (part === null || row.constraint.allowed.length === 0) return null;
    return row;
  }
  if (materialList(raw).length > 1) return row;
  const material = shortMappingValue(raw);
  if (material === null || part === null) return null;
  const name = shortMappingValue(row.name);
  return {
    ...row,
    material,
    source_label: name ?? part,
  };
};

/**
 * A catalog range stays that range. A material label with no narrowed
 * constraint becomes a material constraint. A max building height stays a
 * height row.
 */
const constraintForStoredRow = (
  row: BuildingPartRequirementAi,
): BuildingPartConstraint | null => {
  const narrowed = narrowBuildingPartConstraint(row.constraint);
  if (narrowed !== null && isMaxBuildingHeight(narrowed)) return narrowed;
  if (narrowed !== null && narrowed.kind !== 'text') return narrowed;
  const name = blankToNull(row.name);
  if (
    row.applies_to === 'building_part' &&
    name !== null &&
    (narrowed === null || narrowed.kind === 'text')
  ) {
    return { kind: 'material', allowed: [] };
  }
  return narrowed;
};

const elementFromFields = (input: {
  buildingId: string | null;
  place: string | null;
  name: string | null;
  share: number | null;
  sbef: string | null;
  allowed: readonly BuildingPartMaterial[];
}): ElementRequirement | null => {
  const buildingId = blankToNull(input.buildingId);
  const place = shortMappingValue(input.place);
  const word = shortMappingValue(input.name);
  const sbef = blankToNull(input.sbef);
  if (buildingId === null || place === null || word === null || sbef === null) {
    return null;
  }
  return {
    type: 'element',
    buildingId,
    place,
    name: word,
    share: input.share,
    sbef,
    constraint: { kind: 'material', allowed: [...input.allowed] },
  };
};

const enrichElement = (
  row: BuildingPartRequirementAi,
): ElementRequirement | null =>
  elementFromFields({
    buildingId: row.buildingId,
    place: row.place,
    name: row.name,
    share: row.share,
    sbef: row.sbef,
    allowed:
      row.constraint.kind === 'material' && row.constraint.allowed !== null
        ? row.constraint.allowed
        : [],
  });

const legacyBase = (
  row: BuildingPartRequirementAi,
  constraint: BuildingPartConstraint,
): LegacyBuildingPartRequirement => {
  const height = isMaxBuildingHeight(constraint);
  const partName = shortMappingValue(row.name);
  return {
    applies_to: row.applies_to,
    source_label: height
      ? row.source_label.trim()
      : (partName ?? row.source_label),
    typcode: null,
    building_instance_key: blankToNull(row.building_instance_key),
    evidence_excerpt: row.evidence_excerpt,
    confidence: row.confidence,
    name: partName,
    building_part: { ...EMPTY_PRINTED_CODES },
    material: null,
    share: null,
    constraint,
    typcode_name: null,
    sbef_code: null,
  };
};

/**
 * An element row is the material on one part. A whole-building cap stays a
 * cap. A range or limit that is not that cap stays on the cap schema.
 * Returns null when an element row is missing a field the row requires.
 */
export const enrichBuildingPartRequirement = (
  row: BuildingPartRequirementAi,
): BuildingPartRequirement | null => {
  const constraint = constraintForStoredRow(row);
  if (constraint === null) return null;
  if (row.applies_to === 'building' || isMaxBuildingHeight(constraint)) {
    return keepStoredRequirement(legacyBase(row, constraint));
  }
  if (constraint.kind === 'material') return enrichElement(row);
  const entry =
    row.typcode === null ? undefined : getTypbeteckning(row.typcode);
  return keepStoredRequirement({
    ...legacyBase(row, constraint),
    typcode: row.typcode,
    typcode_name: entry?.name ?? null,
    applies_to: 'building_part',
  });
};

const withDefaultAppliesTo = (value: unknown): unknown => {
  if (!guards.is.object(value)) return value;
  if (Object.hasOwn(value, 'applies_to')) return value;
  return { ...value, applies_to: 'building_part' };
};

/** Rows stored before the material fields existed still parse. The model always emits them. */
const withDefaultMaterialFields = (value: unknown): unknown => {
  if (!guards.is.object(value)) return value;
  const { target: _target, scope: _scope, ...rest } = value;
  const printed = guards.is.object(rest.building_part)
    ? { ...EMPTY_PRINTED_CODES, ...rest.building_part }
    : { ...EMPTY_PRINTED_CODES };
  return {
    name: null,
    material: null,
    share: null,
    ...rest,
    building_part: printed,
  };
};

const withDefaultTextConstraint = (value: unknown): unknown => {
  if (!guards.is.object(value) || !guards.is.object(value.constraint)) {
    return value;
  }
  return {
    ...value,
    constraint: {
      text: null,
      required: null,
      comparison: null,
      number: null,
      unit: null,
      ...value.constraint,
    },
  };
};

const stripElementProvenance = (value: Record<string, unknown>): unknown => {
  const {
    confidence: _confidence,
    sourceFileId: _sourceFileId,
    sourceFileName: _sourceFileName,
    classificationSegmentIndex: _classificationSegmentIndex,
    sources: _sources,
    conflicts: _conflicts,
    ...rest
  } = value;
  return rest;
};

/**
 * A stored material row from before the element shape. The part becomes
 * `place`, the material word becomes `name`, and a stated share becomes
 * 0–1. A missing share, building, label, or SBEF is not invented.
 */
const adoptLegacyMaterial = (
  row: LegacyBuildingPartRequirement,
): ElementRequirement | null => {
  if (row.applies_to !== 'building_part') return null;
  if (row.constraint.kind !== 'material') return null;
  return elementFromFields({
    buildingId: row.building_instance_key,
    place: row.name ?? row.source_label,
    name: row.material,
    share: parseShare(row.share),
    sbef: row.building_part.sbef ?? row.sbef_code,
    allowed: row.constraint.allowed,
  });
};

const adoptLegacy = (
  row: LegacyBuildingPartRequirement,
): BuildingPartRequirement | null => {
  if (row.applies_to === 'building' || row.constraint.kind !== 'material') {
    return keepStoredRequirement(row);
  }
  return adoptLegacyMaterial(row);
};

/**
 * Element row, cap, or AI-flat row narrowed via {@link enrichBuildingPartRequirement}.
 * A material row stored before the element shape is adopted when it already
 * has a building, a part, a label, a share, and an SBEF. Rows persisted
 * before `applies_to` existed are treated as `building_part`.
 */
export const parseBuildingPartRequirement = (
  value: unknown,
): BuildingPartRequirement | null => {
  if (guards.is.object(value) && value.type === 'element') {
    const element = elementRequirementSchema.safeParse(
      stripElementProvenance(value),
    );
    if (element.success) return element.data;
  }
  const normalized = withDefaultMaterialFields(withDefaultAppliesTo(value));
  const stored = buildingPartRequirementSchema.safeParse(normalized);
  if (stored.success) return adoptLegacy(stored.data);
  const ai = buildingPartRequirementAiSchema.safeParse(
    withDefaultTextConstraint(value),
  );
  if (!ai.success) return null;
  return enrichBuildingPartRequirement(ai.data);
};

export const enrichBuildingPartRequirementsInExtractionData = (
  data: Record<string, unknown>,
): Record<string, unknown> => {
  const raw = data.building_part_requirements;
  if (!Array.isArray(raw)) return data;
  return {
    ...data,
    building_part_requirements: raw.flatMap((item) => {
      const parsed = parseBuildingPartRequirement(item);
      if (parsed === null) return [];
      const confidence =
        guards.is.object(item) && typeof item.confidence === 'number'
          ? item.confidence
          : undefined;
      return publishMappableRows(parsed).map((piece) =>
        confidence === undefined ? piece : { ...piece, confidence },
      );
    }),
  };
};

/**
 * Words that name a building or a part of one when followed by a letter or
 * number. Longer tokens precede their prefixes (`huskropp` before `hus`).
 */
const PART_QUALIFIER_TOKENS = [
  'byggnadsdel',
  'byggnad',
  'huskropp',
  'hus',
  'kvarter',
  'kv',
  'trapphus',
  'lamell',
  'etapp',
  'block',
  'del',
] as const;

// `_`/`-` count as boundaries so `part_2` and `hus-2` match; JS `\b` skips `_`.
const PART_QUALIFIER_RE = new RegExp(
  `(?:^|[^a-z0-9])(${PART_QUALIFIER_TOKENS.join('|')})[\\s_.-]*([a-z]|\\d+)(?=$|[^a-z0-9])`,
  'gi',
);

/**
 * Building sub-parts named in the printed label ("phase 2"), sorted.
 * A reference to the row's own building ("Hus 1" on `hus_1`) is not a
 * qualifier. Evidence is ignored: one excerpt often quotes several parts.
 */
const requirementPartQualifier = (row: BuildingPartRequirement): string => {
  if (isElementRequirement(row)) return '';
  const found = new Set<string>();
  for (const match of row.source_label.matchAll(PART_QUALIFIER_RE)) {
    const text = `${match[1]} ${match[2]}`;
    const key =
      extractBuildingReference(text, null)?.key ??
      normalizeBuildingInstanceKey(text);
    if (key !== null && key !== row.building_instance_key) found.add(key);
  }
  return [...found].sort().join('+');
};

const requirementTargetKey = (row: BuildingPartRequirement): string => {
  if (isElementRequirement(row)) return row.place.toLowerCase();
  switch (row.applies_to) {
    case 'building':
      return '';
    case 'building_part': {
      const named = row.name?.trim();
      if (named) return named.toLowerCase();
      return row.typcode ?? row.source_label.trim().toLowerCase();
    }
    default:
      return assertUnreachable(row.applies_to);
  }
};

const constraintSubjectKey = (constraint: BuildingPartConstraint): string => {
  switch (constraint.kind) {
    case 'material':
      return 'material';
    case 'range':
      return `range:${constraint.parameter_id}`;
    case 'limit':
      return `limit:${constraint.comparison}:${constraint.number}:${constraint.unit}`;
    case 'text':
      return 'text';
    default:
      return assertUnreachable(constraint);
  }
};

/**
 * What the requirement is about, independent of its value and wording:
 * building + part qualifier + byggdel + constrained parameter.
 * SBEF does not identify a row: the same code with different materials
 * stays separate rows.
 */
const normalizeRequirementText = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, ' ');

const limitOrTextIdentity = (row: LegacyBuildingPartRequirement): string => {
  switch (row.constraint.kind) {
    case 'text':
      return [
        normalizeRequirementText(row.source_label),
        normalizeRequirementText(row.constraint.text),
      ].join('|');
    case 'limit':
      return [
        normalizeRequirementText(row.source_label),
        row.constraint.comparison,
        String(row.constraint.number),
        normalizeRequirementText(row.constraint.unit),
      ].join('|');
    case 'range':
      return normalizeRequirementText(row.source_label);
    case 'material':
      return '';
    default:
      return assertUnreachable(row.constraint);
  }
};

/**
 * Prose rows stay distinct per printed label and sentence. The same sentence
 * extracted twice still shares one key, so {@link mergeBuildingPartRequirementRows}
 * keeps a single row and its sources. Two numeric limits stay two rows.
 * A range is keyed on the printed subject. The part name is not that id.
 * Different materials stay different rows, including when SBEF is the same.
 * Littra is a display label and does not split a row. An absent share does
 * not split a row and is not invented.
 */
const catalogMaterialKey = (row: BuildingPartRequirement): string => {
  if (row.constraint.kind !== 'material') return '';
  return [...row.constraint.allowed].sort().join(',');
};

const printedMaterialKey = (row: LegacyBuildingPartRequirement): string =>
  [
    row.constraint.kind === 'range' ? '' : (row.name ?? ''),
    row.material ?? '',
    row.share ?? '',
    row.building_part.sbef ?? '',
    catalogMaterialKey(row),
  ]
    .map(normalizeRequirementText)
    .join('|');

const elementIdentityKey = (row: ElementRequirement): string =>
  [
    'element',
    row.buildingId,
    row.place,
    row.name,
    String(row.share),
    row.sbef,
    [...row.constraint.allowed].sort().join(','),
  ]
    .map(normalizeRequirementText)
    .join('|');

const requirementIdentityKey = (row: BuildingPartRequirement): string => {
  if (isElementRequirement(row)) return elementIdentityKey(row);
  const textIdentity = limitOrTextIdentity(row);
  return [
    row.applies_to,
    row.building_instance_key ?? '',
    requirementPartQualifier(row),
    requirementTargetKey(row),
    constraintSubjectKey(row.constraint),
    textIdentity,
    printedMaterialKey(row),
  ].join('|');
};

/** Materials never conflict — allowed lists union. Ranges agree only when both bounds match. */
const constraintValueKey = (constraint: BuildingPartConstraint): string => {
  switch (constraint.kind) {
    case 'material':
      return 'material';
    case 'range':
      return `${constraint.min ?? ''}..${constraint.max ?? ''}`;
    case 'limit':
      return `${constraint.comparison}:${constraint.number}:${normalizeRequirementText(constraint.unit)}`;
    case 'text':
      return normalizeRequirementText(constraint.text);
    default:
      return assertUnreachable(constraint);
  }
};

const unionConstraints = (
  a: BuildingPartConstraint,
  b: BuildingPartConstraint,
): BuildingPartConstraint => {
  if (a.kind !== 'material' || b.kind !== 'material') return a;
  return {
    kind: 'material',
    allowed: [...new Set([...a.allowed, ...b.allowed])],
  };
};

const sourceIdentity = (source: BuildingPartRequirementSource): string =>
  [
    source.sourceFileId,
    source.classificationSegmentIndex,
    source.source_label,
    source.evidence_excerpt,
  ].join('|');

const primarySourceOf = (
  row: BuildingPartRequirementAggregated,
): BuildingPartRequirementSource => ({
  sourceFileId: row.sourceFileId,
  sourceFileName: row.sourceFileName,
  classificationSegmentIndex: row.classificationSegmentIndex,
  source_label: isElementRequirement(row) ? row.name : row.source_label,
  evidence_excerpt: isElementRequirement(row) ? '' : row.evidence_excerpt,
  confidence: row.confidence,
});

const readingsOf = (
  row: BuildingPartRequirementAggregated,
): BuildingPartRequirementReading[] => [
  {
    constraint: row.constraint,
    sources:
      row.sources === undefined || row.sources.length === 0
        ? [primarySourceOf(row)]
        : row.sources,
  },
  ...(row.conflicts ?? []),
];

const addReading = (
  byValue: Map<string, BuildingPartRequirementReading>,
  reading: BuildingPartRequirementReading,
): void => {
  const key = constraintValueKey(reading.constraint);
  const existing = byValue.get(key);
  if (existing === undefined) {
    byValue.set(key, { ...reading, sources: [...reading.sources] });
    return;
  }
  const seen = new Set(existing.sources.map(sourceIdentity));
  byValue.set(key, {
    constraint: unionConstraints(existing.constraint, reading.constraint),
    sources: [
      ...existing.sources,
      ...reading.sources.filter((s) => !seen.has(sourceIdentity(s))),
    ],
  });
};

const maxConfidence = (reading: BuildingPartRequirementReading): number =>
  Math.max(...reading.sources.map((s) => s.confidence));

/** Most agreeing sources, then highest confidence, then first extracted. */
const compareReadings = (
  a: BuildingPartRequirementReading,
  b: BuildingPartRequirementReading,
): number =>
  b.sources.length - a.sources.length || maxConfidence(b) - maxConfidence(a);

const mergeRequirementGroup = (
  group: readonly BuildingPartRequirementAggregated[],
): BuildingPartRequirementAggregated | null => {
  const [head] = group;
  if (head === undefined) return null;
  const byValue = new Map<string, BuildingPartRequirementReading>();
  for (const row of group) {
    for (const reading of readingsOf(row)) addReading(byValue, reading);
  }
  const [picked, ...conflicts] = [...byValue.values()].sort(compareReadings);
  if (picked === undefined) return null;
  const primary = picked.sources[0];
  if (primary === undefined) return null;
  if (isElementRequirement(head)) {
    const constraint =
      picked.constraint.kind === 'material'
        ? picked.constraint
        : head.constraint;
    return {
      ...head,
      constraint,
      confidence: maxConfidence(picked),
      sources: picked.sources,
      conflicts,
    };
  }
  const littra = printedLittra(group);
  return {
    ...head,
    ...primary,
    building_part: { ...head.building_part, littra },
    source_label: isMaxBuildingHeight(picked.constraint)
      ? primary.source_label
      : (head.name ?? primary.source_label),
    constraint: picked.constraint,
    confidence: maxConfidence(picked),
    sources: picked.sources,
    conflicts,
  };
};

/** Littra is display only. The merged row keeps one printed label. */
const printedLittra = (
  group: readonly BuildingPartRequirementAggregated[],
): string | null => {
  for (const row of group) {
    if (isElementRequirement(row)) continue;
    const littra = blankToNull(row.building_part.littra);
    if (littra !== null) return littra;
  }
  return null;
};

/**
 * One row per {@link requirementIdentityKey}. The same printed subject
 * extracted twice merges its sources. A range keeps that subject as its id
 * and does not use the part name. Different range values for one subject
 * keep the best-supported value and list the rest in `conflicts`. Two
 * materials stay two rows, including when they share one SBEF. Idempotent
 * on its own output.
 */
export const mergeBuildingPartRequirementRows = (
  rows: readonly BuildingPartRequirementAggregated[],
): BuildingPartRequirementAggregated[] => {
  const groups = new Map<string, BuildingPartRequirementAggregated[]>();
  for (const row of rows) {
    const key = requirementIdentityKey(row);
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }
  return [...groups.values()]
    .map(mergeRequirementGroup)
    .filter((row): row is BuildingPartRequirementAggregated => row !== null);
};

/**
 * Concatenates enriched building-part requirements from successful file
 * extractions, then merges duplicate byggdel rows.
 */
export const collectBuildingPartRequirementsFromFiles = (
  files: readonly Pick<
    ProjectFile,
    'id' | 'fileName' | 'updatedAt' | 'extractionOutput'
  >[],
): BuildingPartRequirementAggregated[] => {
  const ordered = [...files].sort((a, b) =>
    a.updatedAt.localeCompare(b.updatedAt),
  );
  const collected: BuildingPartRequirementAggregated[] = [];
  for (const file of ordered) {
    const eo = file.extractionOutput;
    if (eo == null || !isProjectFileExtractionOutputV1(eo)) continue;
    for (const seg of eo.segments) {
      if (!seg.ok || seg.data == null) continue;
      const raw = (seg.data as Record<string, unknown>)
        .building_part_requirements;
      if (!Array.isArray(raw)) continue;
      for (const item of raw) {
        const row = parseBuildingPartRequirement(item);
        if (row === null) continue;
        for (const piece of publishMappableRows(row)) {
          const confidence =
            guards.is.object(item) && typeof item.confidence === 'number'
              ? item.confidence
              : isElementRequirement(piece)
                ? 0
                : piece.confidence;
          collected.push({
            ...piece,
            confidence,
            sourceFileId: file.id,
            sourceFileName: file.fileName,
            classificationSegmentIndex: seg.classificationSegmentIndex,
          });
        }
      }
    }
  }
  return mergeBuildingPartRequirementRows(collected);
};

const foldRequirementText = (value: string): string =>
  value
    .toLocaleLowerCase('sv')
    .replace(/å/g, 'a')
    .replace(/ä/g, 'a')
    .replace(/ö/g, 'o');

/** Comparison key: case, punctuation, and Swedish diacritics do not split a row. */
const normalizeCardText = (value: string): string =>
  foldRequirementText(value)
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const requirementBlob = (row: LegacyBuildingPartRequirement): string => {
  const text = row.constraint.kind === 'text' ? row.constraint.text : '';
  return `${row.source_label} ${text}`.trim();
};

const SOUND_UNIT_RE = /^(?:db|dba)$/i;

/**
 * Sound stays off the requirement cards. A limit whose unit is dB or dBA
 * is sound even when the heading does not say so.
 */
const isSoundRequirement = (row: LegacyBuildingPartRequirement): boolean => {
  if (
    row.constraint.kind === 'limit' &&
    SOUND_UNIT_RE.test(row.constraint.unit.trim())
  ) {
    return true;
  }
  return /(?:\bdba\b|\bdb\b|ljud|buller|akustik)/.test(
    foldRequirementText(requirementBlob(row)),
  );
};

/**
 * A card is a requirement on a building part. Building-level rows and sound
 * rows stay in storage and are not shown.
 */
const isShownRequirementCard = (row: BuildingPartRequirement): boolean => {
  if (isElementRequirement(row)) return true;
  switch (row.applies_to) {
    case 'building':
      return false;
    case 'building_part':
      return !isSoundRequirement(row);
    default:
      return assertUnreachable(row.applies_to);
  }
};

const requirementPartKey = (row: LegacyBuildingPartRequirement): string => {
  if (row.typcode != null) return `typ:${row.typcode}`;
  const label = normalizeCardText(row.source_label);
  const sbef = row.sbef_code?.trim() ?? '';
  if (sbef !== '') return `sbef:${sbef}|${label}`;
  return `label:${label}`;
};

const materialSetKey = (row: BuildingPartRequirement): string => {
  if (row.constraint.kind !== 'material') return '';
  return [...row.constraint.allowed].sort().join(',');
};

const structuredFieldKey = (row: LegacyBuildingPartRequirement): string =>
  [
    normalizeCardText(row.name ?? ''),
    normalizeCardText(row.material ?? ''),
    normalizeCardText(row.share ?? ''),
    (row.building_part.sbef ?? '').trim(),
  ].join('|');

const requirementCardKey = (row: BuildingPartRequirement): string => {
  if (isElementRequirement(row)) return elementIdentityKey(row);
  const building = row.building_instance_key ?? '';
  switch (row.constraint.kind) {
    case 'material':
      return `${building}|material|${materialSetKey(row)}|${structuredFieldKey(row)}|${requirementPartKey(row)}`;
    case 'range':
      return `${building}|range|${row.constraint.parameter_id}|${row.constraint.min ?? ''}|${row.constraint.max ?? ''}|${requirementPartKey(row)}`;
    case 'limit':
      return `${building}|limit|${row.constraint.comparison}|${row.constraint.number}|${normalizeCardText(row.constraint.unit)}|${normalizeCardText(row.source_label)}`;
    case 'text':
      return `${building}|text|${normalizeCardText(row.constraint.text)}`;
    default:
      return assertUnreachable(row.constraint);
  }
};

const rowWithSources = (
  row: BuildingPartRequirementAggregated,
): BuildingPartRequirementAggregated => {
  const sources =
    row.sources === undefined || row.sources.length === 0
      ? [primarySourceOf(row)]
      : [...row.sources];
  return { ...row, sources };
};

/** One card per key. Sources are combined. The input rows are not changed. */
const dedupeRequirementCards = (
  rows: readonly BuildingPartRequirementAggregated[],
  keyOf: (row: BuildingPartRequirementAggregated) => string,
): BuildingPartRequirementAggregated[] => {
  const groups = new Map<string, BuildingPartRequirementAggregated[]>();
  for (const row of rows) {
    const key = keyOf(row);
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }
  const merged: BuildingPartRequirementAggregated[] = [];
  for (const group of groups.values()) {
    const ranked = [...group].sort((a, b) => {
      const sbefRank = (row: BuildingPartRequirementAggregated): number =>
        isElementRequirement(row) ? 1 : row.sbef_code != null ? 1 : 0;
      const typcodeRank = (row: BuildingPartRequirementAggregated): number =>
        isElementRequirement(row) ? 0 : row.typcode_name != null ? 1 : 0;
      const sbef = sbefRank(b) - sbefRank(a);
      if (sbef !== 0) return sbef;
      const name = typcodeRank(b) - typcodeRank(a);
      if (name !== 0) return name;
      return (
        (b.sources?.length ?? 1) - (a.sources?.length ?? 1) ||
        b.confidence - a.confidence
      );
    });
    const [first, ...rest] = ranked;
    if (first === undefined) continue;
    const head = rowWithSources(first);
    const seen = new Set((head.sources ?? []).map(sourceIdentity));
    const sources = [...(head.sources ?? [])];
    for (const row of rest) {
      for (const source of rowWithSources(row).sources ?? []) {
        const id = sourceIdentity(source);
        if (seen.has(id)) continue;
        seen.add(id);
        sources.push(source);
      }
    }
    merged.push({ ...head, sources });
  }
  return merged;
};

/**
 * Building-part cards for the requirement view. Building-level rows and sound
 * rows are omitted. A material list becomes one row per material. A finish,
 * a sentence, and a row with no labeled field are omitted. The input is not
 * changed.
 */
export const buildingPartRequirementCards = (
  rows: readonly BuildingPartRequirementAggregated[],
): BuildingPartRequirementAggregated[] =>
  dedupeRequirementCards(
    rows
      .flatMap((row) => publishMappableRows(row))
      .filter(isShownRequirementCard)
      .filter((row) => requirementCardFields(row).length > 0),
    requirementCardKey,
  );

/**
 * One material per row. A comma or "och" list becomes several rows. A
 * paragraph stays unsplit and is not used as a material.
 */
const materialsOnRow = (row: LegacyBuildingPartRequirement): string[] => {
  const printed = blankToNull(row.material);
  if (printed !== null) {
    const listed = materialList(printed);
    return listed.length > 1 ? listed : [printed];
  }
  if (row.constraint.kind !== 'text') return [];
  return materialList(row.constraint.text);
};

const withMaterial = <T extends LegacyBuildingPartRequirement>(
  row: T,
  material: string,
): T => ({
  ...row,
  material,
  constraint:
    row.constraint.kind === 'text'
      ? { kind: 'material', allowed: [] }
      : row.constraint,
});

/** A comma or "och" list becomes one row per material. A paragraph does not. */
const expandMaterialRows = <T extends LegacyBuildingPartRequirement>(
  row: T,
): T[] => {
  const materials = materialsOnRow(row);
  if (materials.length < 2) return [row];
  return materials.map((material) => withMaterial(row, material));
};

const expandElementNames = (row: ElementRequirement): ElementRequirement[] => {
  const names = materialList(row.name);
  if (names.length < 2) return [row];
  return names.flatMap((name) => {
    const next = elementFromFields({
      buildingId: row.buildingId,
      place: row.place,
      name,
      share: row.share,
      sbef: row.sbef,
      allowed: row.constraint.allowed,
    });
    return next === null ? [] : [next];
  });
};

function publishMappableRows<T extends BuildingPartRequirement>(row: T): T[] {
  if (isElementRequirement(row)) {
    return expandElementNames(row).map(
      (element) => ({ ...row, ...element }) as T,
    );
  }
  return expandMaterialRows(row).flatMap((piece) => {
    const kept = keepStoredRequirement(piece);
    if (kept === null) return [];
    return [{ ...row, ...kept } as T];
  });
}

export type RequirementCardField = {
  label: 'Byggdel' | 'Material' | 'Andel' | 'SBEF' | 'Littra' | 'Byggnadshöjd';
  value: string;
};

const partNameOnCard = (
  row: Pick<
    LegacyBuildingPartRequirement,
    'name' | 'source_label' | 'constraint'
  >,
): string | null => {
  const named = shortMappingValue(row.name);
  if (named !== null) return named;
  const label = shortMappingValue(row.source_label);
  if (label === null) return null;
  if (
    row.constraint.kind === 'text' &&
    label === shortMappingValue(row.constraint.text)
  ) {
    return null;
  }
  return label;
};

const heightBound = (constraint: BuildingPartConstraint): string | null => {
  if (!isMaxBuildingHeight(constraint)) return null;
  if (constraint.kind !== 'range') return null;
  const bounds = [
    constraint.min != null ? `≥ ${constraint.min}` : null,
    constraint.max != null ? `≤ ${constraint.max}` : null,
  ].filter((bound): bound is string => bound !== null);
  return bounds.length > 0 ? bounds.join(' ') : null;
};

/**
 * Labeled fields for one Krav card. An element card is the part, the
 * label, the share, and SBEF. A leftover material card shows the fields
 * that row still has. A height row is its numeric bound.
 */
export const requirementCardFields = (
  row:
    | ElementRequirement
    | Pick<
        LegacyBuildingPartRequirement,
        | 'name'
        | 'material'
        | 'share'
        | 'source_label'
        | 'constraint'
        | 'building_part'
      >,
): RequirementCardField[] => {
  if (isElementRequirement(row)) {
    const fields: RequirementCardField[] = [
      { label: 'Byggdel', value: row.place },
      { label: 'Material', value: row.name },
    ];
    if (row.share !== null) {
      fields.push({
        label: 'Andel',
        value: formatRequirementShare(row.share),
      });
    }
    fields.push({ label: 'SBEF', value: row.sbef });
    return fields;
  }
  const bound = heightBound(row.constraint);
  if (bound !== null) return [{ label: 'Byggnadshöjd', value: bound }];
  const fields: RequirementCardField[] = [];
  const part = partNameOnCard(row);
  if (part !== null) fields.push({ label: 'Byggdel', value: part });
  const material = shortMappingValue(row.material);
  if (material !== null) fields.push({ label: 'Material', value: material });
  const share = shortMappingValue(row.share);
  if (share !== null) fields.push({ label: 'Andel', value: share });
  const sbef = shortMappingValue(row.building_part.sbef);
  if (sbef !== null) fields.push({ label: 'SBEF', value: sbef });
  const littra = shortMappingValue(row.building_part.littra);
  if (littra !== null) fields.push({ label: 'Littra', value: littra });
  return fields;
};

/** Sort key for a card. Not the card contents. */
export const formatRequirementCardHeading = (
  row: Parameters<typeof requirementCardFields>[0],
): string =>
  requirementCardFields(row)
    .map((field) => `${field.label}: ${field.value}`)
    .join('\n');

/**
 * Field caption for the target: byggdel vs the building as a whole.
 */
export const formatBuildingRequirementScopeCaption = (
  appliesTo: BuildingRequirementAppliesTo,
): string => {
  switch (appliesTo) {
    case 'building':
      return 'Byggnad';
    case 'building_part':
      return 'Byggdel';
    default:
      return assertUnreachable(appliesTo);
  }
};

/**
 * What the row applies to under {@link formatBuildingRequirementScopeCaption}:
 * printed label, plus mapped mellankategori + typcode when that adds information.
 */
export const formatStoredRequirementScopeCaption = (
  row: ElementRequirement | Pick<LegacyBuildingPartRequirement, 'applies_to'>,
): string => {
  if (isElementRequirement(row)) return 'Byggdel';
  return formatBuildingRequirementScopeCaption(row.applies_to);
};

export const formatBuildingPartTarget = (
  row:
    | ElementRequirement
    | Pick<
        LegacyBuildingPartRequirement,
        'applies_to' | 'source_label' | 'typcode' | 'typcode_name'
      >,
): string => {
  if (isElementRequirement(row)) return row.place;
  switch (row.applies_to) {
    case 'building': {
      const printed = row.source_label.trim();
      return printed === '' ? '—' : printed;
    }
    case 'building_part': {
      const printed = row.source_label.trim();
      const mapped = row.typcode_name?.trim() ?? '';
      const code = row.typcode;
      if (
        code != null &&
        mapped !== '' &&
        mapped.toLowerCase() !== printed.toLowerCase()
      ) {
        return `${printed} · ${mapped} (${code})`;
      }
      if (code != null) return `${printed} (${code})`;
      return printed;
    }
    default:
      return assertUnreachable(row.applies_to);
  }
};

const LIMIT_COMPARISON_SIGN = {
  lt: '<',
  lte: '≤',
  gt: '>',
  gte: '≥',
  eq: '=',
} as const satisfies Record<BuildingPartLimitComparison, string>;

/** The requirement itself: material(s) or a numeric bound. */
export const formatBuildingPartConstraint = (
  constraint: BuildingPartConstraint,
): string => {
  switch (constraint.kind) {
    case 'material':
      return constraint.allowed
        .map((m) => BUILDING_PART_MATERIAL_LABELS[m])
        .join(', ');
    case 'range': {
      const bounds = [
        constraint.min != null ? `≥ ${constraint.min}` : null,
        constraint.max != null ? `≤ ${constraint.max}` : null,
      ].filter((x): x is string => x !== null);
      const boundText = bounds.length > 0 ? bounds.join(' ') : 'obegränsat';
      return `${BUILDING_PART_RANGE_PARAMETER_LABELS[constraint.parameter_id]} ${boundText}`;
    }
    case 'limit':
      return `${LIMIT_COMPARISON_SIGN[constraint.comparison]} ${constraint.number} ${constraint.unit}`;
    case 'text':
      return constraint.text;
    default:
      return assertUnreachable(constraint);
  }
};

/** Constraint text shown on a card, including a long material sentence. */
export const formatShownRequirementConstraint = (
  constraint: BuildingPartConstraint,
): string => {
  switch (constraint.kind) {
    case 'material':
    case 'range':
    case 'limit':
      return formatBuildingPartConstraint(constraint);
    case 'text':
      return constraint.text.trim();
    default:
      return assertUnreachable(constraint);
  }
};
